import { useState } from 'react'
import { motion } from 'framer-motion'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import SectionTitle from '../ui/SectionTitle'
import Button       from '../ui/Button'
import { useGallery } from '../../hooks/useGallery'

const FALLBACK_COLORS = [
  '#FFB3D9', '#FFE066', '#C4B5FD',
  '#86EFAC', '#FFB3D9', '#C4B5FD',
]

// Vary aspect ratios for a pseudo-masonry feel in a CSS grid
const ASPECT = ['aspect-[4/5]', 'aspect-square', 'aspect-square', 'aspect-[4/5]', 'aspect-square', 'aspect-square']

export default function GalleryPreview() {
  const { data: gallery, isLoading } = useGallery()
  const [lightboxIndex, setLightboxIndex] = useState(-1)

  const items  = gallery?.slice(0, 6) ?? []
  const slides = items
    .filter((g) => g.image?.url)
    .map((g) => ({ src: g.image.url, alt: g.gallery_caption || '' }))

  return (
    <section className="section-padding bg-gradient-pink">
      <div className="container-custom">
        <SectionTitle
          eyebrow="Our Work"
          title="Magical Moments 📸"
          subtitle="Real photos from our amazing celebrations"
        />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(isLoading ? Array(6).fill(null) : items).map((item, i) =>
            isLoading ? (
              <div key={i} className={`skeleton rounded-2xl ${ASPECT[i]}`} />
            ) : (
              <motion.button
                key={item?.id ?? i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.03 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                onClick={() => setLightboxIndex(i)}
                className={[
                  'group relative overflow-hidden rounded-2xl cursor-pointer w-full',
                  ASPECT[i],
                ].join(' ')}
                style={!item?.image?.url ? { backgroundColor: FALLBACK_COLORS[i] } : {}}
              >
                {item?.image?.url ? (
                  <img
                    src={item.image.url}
                    alt={item.gallery_caption || ''}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl select-none">
                    🎉
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/50 flex items-end p-4 transition-all duration-300">
                  <div className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    {item?.gallery_caption && (
                      <p className="text-white font-body font-semibold text-sm mb-1">
                        {item.gallery_caption}
                      </p>
                    )}
                    <span className="text-white/60 font-body text-xs">🔍 View</span>
                  </div>
                </div>
              </motion.button>
            )
          )}
        </div>

        {slides.length > 0 && (
          <Lightbox
            open={lightboxIndex >= 0}
            index={lightboxIndex}
            close={() => setLightboxIndex(-1)}
            slides={slides}
            styles={{ container: { backgroundColor: 'rgba(26,10,46,0.97)' } }}
          />
        )}

        <div className="text-center mt-12">
          <Button to="/gallery" variant="primary">
            View Full Gallery 📸
          </Button>
        </div>
      </div>
    </section>
  )
}
